import { useState } from "react";
import { api, ApiError } from "../api";
import { PencilIcon } from "../icons";
import type { SessionSummary } from "../types";

/** The longest name the server keeps for a conversation. */
const TITLE_MAX = 200;

/**
 * A conversation's name in the chat header. Its owner renames it in place; the name shows at once and the
 * list is told to catch up.
 */
export function SessionTitle(props: {
	session: Pick<SessionSummary, "id" | "title">;
	canRename: boolean;
	onRenamed: () => void;
	onError: (message: string) => void;
}) {
	const [editing, setEditing] = useState(false);
	/** The name just saved, shown until the session list carries it. */
	const [saved, setSaved] = useState<string | null>(null);
	const title = saved ?? props.session.title ?? "新对话";
	if (editing) {
		return (
			<TitleForm
				initial={props.session.title ?? ""}
				onCancel={() => setEditing(false)}
				onSave={async (name) => {
					try {
						const { title: kept } = await api.renameSession(props.session.id, name);
						setSaved(kept);
						setEditing(false);
						props.onRenamed();
					} catch (err) {
						props.onError(err instanceof ApiError ? `重命名失败：${err.message}` : "重命名失败");
					}
				}}
			/>
		);
	}
	return (
		<span className="title-row">
			<span className="title">{title}</span>
			{props.canRename && (
				<button className="ghost title-edit" aria-label="重命名对话" title="重命名" onClick={() => setEditing(true)}>
					<PencilIcon size={14} />
				</button>
			)}
		</span>
	);
}

function TitleForm(props: { initial: string; onSave: (name: string) => Promise<void>; onCancel: () => void }) {
	const [name, setName] = useState(props.initial);
	const [busy, setBusy] = useState(false);
	return (
		<form
			className="title-form"
			onSubmit={async (e) => {
				e.preventDefault();
				setBusy(true);
				await props.onSave(name.trim());
				setBusy(false);
			}}
		>
			<input
				aria-label="对话名称"
				placeholder="对话名称"
				value={name}
				maxLength={TITLE_MAX}
				autoFocus
				onChange={(e) => setName(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Escape") props.onCancel();
				}}
			/>
			<button type="submit" disabled={busy || !name.trim()}>
				保存
			</button>
			<button type="button" className="ghost" onClick={props.onCancel}>
				取消
			</button>
		</form>
	);
}
